import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, MessageSquare, LifeBuoy, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * SupportCenter — the "Need Help" hub.
 *
 * Three-tier customer service:
 *   Level 1: AI (Mythri concierge) — ~80% of questions answered instantly
 *   Level 2: Direct hotel chat — ~15% (room, check-in, amenities, local Qs)
 *   Level 3: SwaanStay ticket — ~5% (payment, refund, disputes, tech bugs)
 *
 * We route users through this order to keep support costs low while still
 * giving a fast human path when they need one.
 */
export default function SupportCenter() {
  const { user } = useAuth();
  const options = [
    {
      icon: Sparkles,
      title: "Ask Mythri AI",
      desc: "Instant answers on booking status, cancellation rules, refund status, invoices, hotel policies and more. 24/7.",
      cta: "Open AI chat",
      color: "from-emerald-500 to-emerald-700",
      // Mythri is a floating widget — this button opens it via a global event
      action: "mythri",
      testid: "support-open-ai",
    },
    {
      icon: MessageSquare,
      title: "Chat with your hotel",
      desc: "Best for room-level questions: check-in time, parking, extra bed, food, local directions, late check-out.",
      cta: "See my hotel chats",
      color: "from-blue-600 to-indigo-700",
      to: user ? "/bookings" : "/login",
      testid: "support-hotel-chat",
    },
    {
      icon: LifeBuoy,
      title: "Raise a ticket",
      desc: "For payment issues, refund tracking, booking disputes, technical bugs — anything the hotel can't fix.",
      cta: "Create ticket",
      color: "from-orange-500 to-red-600",
      to: user ? "/support/new" : "/login",
      testid: "support-create-ticket",
    },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="hw-label text-blue-700">Support Centre</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3" data-testid="support-heading">
          Need a hand? Pick the fastest path.
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl leading-relaxed">
          Most questions are answered instantly by our AI or your hotel directly. If something needs the SwaanStay team, raise a ticket and we&apos;ll respond within 24 hours.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {options.map((o) => {
            const Icon = o.icon;
            const inner = (
              <div className={`hw-card p-6 h-full transition-transform hover:-translate-y-1 hover:hw-shadow-pop-amber cursor-pointer`} data-testid={o.testid}>
                <div className={`w-12 h-12 rounded-sm bg-gradient-to-br ${o.color} grid place-items-center text-white`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="hw-head text-xl font-black text-slate-900 mt-4">{o.title}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{o.desc}</div>
                <div className="mt-4 text-emerald-700 hover:text-emerald-800 font-bold text-sm inline-flex items-center gap-1">
                  {o.cta} <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            );
            if (o.to) return <Link key={o.title} to={o.to}>{inner}</Link>;
            return (
              <button
                key={o.title}
                type="button"
                className="text-left"
                onClick={() => {
                  if (o.action === "mythri") {
                    if (!user) {
                      toast.error("Please log in as a traveller to chat with Mythri AI.");
                      return;
                    }
                    if (user.role !== "customer") {
                      toast.message("Mythri AI is for travellers", { description: "Hotel partners & admins: use the ticket flow for support." });
                      return;
                    }
                    window.dispatchEvent(new CustomEvent("swaan:open-mythri"));
                    // If the widget isn't already open in the DOM (still hidden on /login etc)
                    // toast a helpful hint after a short delay.
                    setTimeout(() => {
                      const bubble = document.querySelector('[data-testid="concierge-launcher"], [data-testid="concierge-panel"]');
                      if (!bubble) toast.message("Mythri didn't open?", { description: "Refresh the page or go to any hotel page and tap the emerald chat bubble in the bottom-right." });
                    }, 400);
                  }
                }}
              >{inner}</button>
            );
          })}
        </div>

        {/* My tickets shortcut */}
        {user && (
          <div className="mt-6 flex items-center justify-between hw-card p-4 border-slate-200">
            <div className="text-sm text-slate-700">
              <span className="hw-head font-black">Existing tickets?</span> Track status & replies.
            </div>
            <Link to="/support/tickets" className="text-blue-700 hover:text-emerald-700 font-bold text-sm inline-flex items-center gap-1" data-testid="support-my-tickets">
              My tickets <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Booking Protection */}
        <div className="mt-8 hw-card p-6 border-l-4 border-l-emerald-600 bg-emerald-50" data-testid="booking-protection-banner">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-700 flex-shrink-0" />
            <div className="hw-head text-lg font-black text-slate-900">SwaanStay Booking Protection Guarantee</div>
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            If a confirmed booking cannot be honoured by the hotel (overbooking, closure, room not as promised), we&apos;ll arrange a comparable alternative of equal or higher category — or a full refund + ₹500 inconvenience credit — subject to our terms.
          </p>
          <Link to="/booking-protection" className="mt-3 inline-flex text-emerald-700 hover:text-emerald-800 font-bold text-sm items-center gap-1" data-testid="booking-protection-details">
            Read the protection terms <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 text-center text-xs text-slate-500">
          Response times: AI instantly · Hotel typically within 10-30 min · SwaanStay team within 24 hrs (or same day for high-priority).
        </div>
      </div>
    </div>
  );
}
